<script>
import { isNrwVisible } from '@/utils/featureHosts'

// label 은 i18n 키로 — template 에서 $t(menu.labelKey) 로 lookup
//   visible 이 있으면 그 함수가 true 를 돌려줄 때만 메뉴에 나온다
const MENUS = [
  { key: 'monitoring',  labelKey: 'nav.monitoring',  route: '/' },
  { key: 'leak',        labelKey: 'nav.leak',        route: '/water-leak' },
  { key: 'logger',      labelKey: 'nav.logger',      route: '/logger' },
  { key: 'gis-master',  labelKey: 'nav.gis_master',  route: '/gis-master' },
  { key: 'maintenance', labelKey: 'nav.maintenance', route: '/maintenance' },
  // NRW — 로컬은 항상, 운영은 id.neverlosewater.com + 고객사 0·16 일 때만 노출 (라우터에도 같은 가드가 걸려 있음)
  { key: 'nrw',         labelKey: 'nav.nrw',         route: '/nrw', visible: isNrwVisible },
]

export default {
  name: 'LeftNav',
  data() {
    return {
      expanded: false,
      hoveredKey: null,
    }
  },
  computed: {
    menus() {
      return MENUS.filter(m => (typeof m.visible === 'function' ? m.visible() : true))
    },
  },
  methods: {
    toggle() {
      this.expanded = !this.expanded
    },
    navigate(route) {
      this.$router.push(route)
      this.expanded = false
    },
    isActive(route) {
      return this.$route.path === route
    },
    tooltipText(menu) {
      if (menu?.key === 'monitoring') {
        // 모니터링 메뉴만 이름 대신 설명형 툴팁 사용 (언어별)
        return this.$t('nav.monitoring_tooltip')
      }
      return this.$t(menu.labelKey)
    },
  },
}
</script>

<template>
  <nav class="left-nav" :class="{ expanded }">

    <!-- 토글 버튼 -->
    <button class="toggle-btn" @click="toggle" :title="expanded ? $t('nav.collapse') : $t('nav.expand')">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="3" y1="6"  x2="21" y2="6"/>
        <line x1="3" y1="12" x2="21" y2="12"/>
        <line x1="3" y1="18" x2="21" y2="18"/>
      </svg>
    </button>

    <!-- 메뉴 아이템 -->
    <div class="menu-list">
      <div
        v-for="menu in menus"
        :key="menu.key"
        class="menu-item"
        :class="{ active: isActive(menu.route) }"
        @click="navigate(menu.route)"
        @mouseenter="hoveredKey = menu.key"
        @mouseleave="hoveredKey = null"
      >
        <!-- 아이콘 -->
        <span class="menu-icon">
          <!-- Monitoring -->
          <svg v-if="menu.key === 'monitoring'" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="4" width="20" height="13" rx="2"/>
            <polyline points="9 11 11 13 15 8"/>
            <line x1="9" y1="20" x2="15" y2="20"/>
            <line x1="12" y1="17" x2="12" y2="20"/>
          </svg>
          <!-- Water Leak Detection -->
          <svg v-else-if="menu.key === 'leak'" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2C6 8 4 12 4 15a8 8 0 0016 0c0-3-2-7-8-13z"/>
            <path d="M12 22v-4M8 18l2-2M16 18l-2-2"/>
          </svg>
          <!-- Logger -->
          <svg v-else-if="menu.key === 'logger'" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M8 2H18C19.1 2 20 2.9 20 4V20C20 21.1 19.1 22 18 22H6C4.9 22 4 21.1 4 20V13L6 11V4C6 2.9 6.9 2 8 2Z"/>
            <rect x="9" y="4" width="2" height="4"/>
            <rect x="12" y="4" width="2" height="4"/>
            <rect x="15" y="4" width="2" height="4"/>
            <path d="M6 14h2l1.5-3 2 6 1.5-4h2.5l1 2h1.5"/>
            <circle cx="18.5" cy="15" r="1" fill="currentColor" stroke="none"/>
          </svg>
          <!-- GIS Master -->
          <svg v-else-if="menu.key === 'gis-master'" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
            <line x1="8" y1="2" x2="8" y2="18"/>
            <line x1="16" y1="6" x2="16" y2="22"/>
          </svg>
          <!-- Maintenance -->
          <svg v-else-if="menu.key === 'maintenance'" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
          </svg>
          <!-- NRW (Non-Revenue Water) — 물방울 + 퍼센트 -->
          <svg v-else-if="menu.key === 'nrw'" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M7 2C3 6.5 1.5 9.3 1.5 11.5a5.5 5.5 0 0011 0C12.5 9.3 11 6.5 7 2z"/>
            <line x1="15" y1="20" x2="22" y2="9"/>
            <circle cx="16" cy="10.5" r="1.6"/>
            <circle cx="21" cy="18.5" r="1.6"/>
          </svg>
        </span>

        <!-- 라벨 (expanded 상태에서만) -->
        <span v-if="expanded" class="menu-label">{{ $t(menu.labelKey) }}</span>

        <!-- 툴팁 (collapsed + hover 시) -->
        <div
          v-if="!expanded && hoveredKey === menu.key"
          class="menu-tooltip"
        >
          {{ tooltipText(menu) }}
        </div>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.left-nav {
  width: 56px;
  height: 100%;
  background-color: var(--bg-base);
  border-right: 1px solid var(--border-subtle);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  transition: width 0.22s ease;
  overflow: visible;
  z-index: 100;
}

.left-nav.expanded {
  width: 210px;
}

/* 토글 버튼 */
.toggle-btn {
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-bottom: 1px solid var(--border-subtle);
  color: var(--text-disabled);
  cursor: pointer;
  flex-shrink: 0;
  transition: color .2s, background .2s;
}
.toggle-btn:hover {
  color: var(--accent);
  background: rgba(var(--accent-rgb),.06);
}

/* 메뉴 리스트 */
.menu-list {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0;
  gap: 2px;
}

/* 메뉴 아이템 */
.menu-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 0 0 0;
  height: 48px;
  cursor: pointer;
  color: var(--text-disabled);
  border-left: 2px solid transparent;
  transition: color .2s, background .2s, border-color .2s;
  white-space: nowrap;
  overflow: visible;
}
.menu-item:hover {
  color: var(--text-muted);
  background: var(--left-nav-hover-bg);
}
/* 활성 메뉴 — 아이콘 주위에 둥근 시안 박스. 행 배경/좌측라인은 제거 */
.menu-item.active {
  color: var(--accent);
  background: transparent;
  border-left-color: transparent;
}
.menu-item.active .menu-icon {
  position: relative;
}
.menu-item.active .menu-icon::before {
  content: '';
  position: absolute;
  inset: 5px 9px;
  border-radius: 10px;
  background: var(--left-nav-active-bg);
  border: 1px solid rgba(var(--accent-rgb), .55);
  z-index: 0;
}
.menu-item.active .menu-icon svg {
  position: relative;
  z-index: 1;
}

.menu-icon {
  width: 56px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.menu-label {
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: .02em;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 툴팁 */
.menu-tooltip {
  position: absolute;
  left: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  background: rgba(10,18,32,.96);
  border: 1px solid var(--border-subtle);
  border-radius: 0.375rem;
  padding: 5px 10px;
  font-size: 0.8125rem;
  color: var(--text-primary);
  white-space: nowrap;
  pointer-events: none;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0,0,0,.4);
}
.menu-tooltip::before {
  content: '';
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 5px solid transparent;
  border-right-color: var(--border-subtle);
}

/* 라이트 테마 — 좌측 메뉴바를 진한 네이비 솔리드로 (헤더바와 동일 프레임). 활성은 sky 포인트 */
:root[data-theme="light"] .left-nav {
  background: var(--bar-bg);
  border-right-color: rgba(255,255,255,.10);
}
:root[data-theme="light"] .toggle-btn {
  color: var(--bar-fg-muted);
  border-bottom-color: var(--bar-border);
}
:root[data-theme="light"] .toggle-btn:hover {
  color: #fff;
  background: var(--bar-hover-bg);
}
:root[data-theme="light"] .menu-item {
  color: var(--bar-fg-muted);
  border-left-color: transparent;
}
:root[data-theme="light"] .menu-item:hover {
  color: var(--bar-fg);
  background: var(--bar-hover-bg);
}
/* 활성 메뉴 — 아이콘 주위에 둥근 시안 박스 (이미지 스타일). 행 배경/좌측라인은 제거 */
:root[data-theme="light"] .menu-item.active {
  color: var(--bar-point);
  background: transparent;
  border-left-color: transparent;
}
:root[data-theme="light"] .menu-item.active .menu-icon {
  position: relative;
}
:root[data-theme="light"] .menu-item.active .menu-icon::before {
  content: '';
  position: absolute;
  inset: 5px 9px;
  border-radius: 10px;
  background: var(--bar-active-bg);
  border: 1px solid rgba(34,211,238,.55);
  z-index: 0;
}
:root[data-theme="light"] .menu-item.active .menu-icon svg {
  position: relative;
  z-index: 1;
}
/* 툴팁 — 라이트 테마에선 글자색(--text-primary)이 진해 어두운 배경과 안 맞음.
   메뉴바와 동일한 네이비 배경 + 밝은 글자로 조정 */
:root[data-theme="light"] .menu-tooltip {
  background: var(--bar-bg);
  color: var(--bar-fg);
  border-color: var(--bar-border);
  box-shadow: 0 4px 12px rgba(15,23,42,.2);
}
:root[data-theme="light"] .menu-tooltip::before {
  border-right-color: var(--bar-border);
}
</style>
